import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Alert, Snackbar } from "@mui/material";

type ToastSeverity = "success" | "info" | "warning" | "error";
type Toast = { id: number; message: string; severity: ToastSeverity };

type ToastApi = {
  show: (message: string, severity?: ToastSeverity) => void;
  success: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
  error: (message: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

/**
 * App-wide snackbar provider. Use `useToast()` from any page to surface a
 * one-line message. Single toast at a time (newer message replaces older);
 * auto-dismiss after 4s, or manual close via the alert's X button.
 *
 * Replaces the per-page `<Typography color=error>` + ad-hoc `<Snackbar>`
 * sprinkles — every mutation can now `toast.success("Хадгаллаа")` without
 * reaching for local state.
 */
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toast, setToast] = useState<Toast | null>(null);

  const show = useCallback((message: string, severity: ToastSeverity = "info") => {
    setToast({ id: Date.now(), message, severity });
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (m) => show(m, "success"),
      info: (m) => show(m, "info"),
      warning: (m) => show(m, "warning"),
      error: (m) => show(m, "error"),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        key={toast?.id}
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert
            onClose={() => setToast(null)}
            severity={toast.severity}
            variant="filled"
            sx={{ minWidth: 280 }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
